import Button from "components/button";
import ImageUploadPreview from "components/shared/image-upload-preview";
import { DriverFormValues } from "_interfaces/driver.interfaces";
import Icon from "components/icon";
import Input, { Textarea } from "components/input";
import PhoneInput from "components/input/phone-input";
import Select from "components/select";
import Typography from "components/typography";
import { NamespaceKeys } from "locale/types";
import { Controller, UseFormReturn } from "react-hook-form";
import DriverAccordion from "./driver-accordion";
import DatePicker from "components/date-picker";

interface DriverProfileAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onNext: () => void;
  form: UseFormReturn<DriverFormValues>;
  t: (key: NamespaceKeys<"driver">, options?: Record<string, unknown>) => string;
  vendorOptions: Array<{ label: string; value: string; subtitle?: string }>;
  isLoadingVendorOptions: boolean;
  onSearchVendors: (search: string) => Promise<Array<{ label: string; value: string; subtitle?: string }>>;
  driverPictureName: string;
  driverPictureUrl: string;
  isUploadingDriverPicture: boolean;
  onDriverPictureChange: (file?: File) => void | Promise<void>;
  driverPictureError?: string;
}

const DriverProfileAccordion = ({
  isOpen,
  onToggle,
  onNext,
  form,
  t,
  vendorOptions,
  isLoadingVendorOptions,
  onSearchVendors,
  driverPictureName,
  driverPictureUrl,
  isUploadingDriverPicture,
  onDriverPictureChange,
  driverPictureError,
}: DriverProfileAccordionProps) => {
  const {
    register,
    watch,
    formState: { errors },
  } = form;

  const isSameAddress = watch("isSameAddress");

  return (
    <DriverAccordion
      title={t("accordion-driver-profile")}
      subtitle={t("accordion-driver-profile-subtitle")}
      iconName="profile-2user"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="grid grid-cols-[172px_1fr] gap-4">
        <div className="flex flex-col gap-1.5">
          <Typography variant="bodyLarge">
            {t("label-driver-picture")} <span className="text-error-500">*</span>
          </Typography>
          <ImageUploadPreview
            id="upload-driver-picture"
            dataTestId="upload-driver-picture"
            value={driverPictureUrl}
            fileName={driverPictureName}
            onUpload={(file) => onDriverPictureChange(file)}
            onRemove={() => onDriverPictureChange(undefined)}
            isUploading={isUploadingDriverPicture}
            dropText={t("upload-driver-picture-placeholder")}
            helperText={t("upload-driver-picture-helper")}
            accept=".jpg,.jpeg,.png"
            error={driverPictureError}
          />
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Input
            label={t("label-driver-name")}
            required
            placeholder={t("placeholder-driver-name")}
            data-testid="input-driver-name"
            {...register("driverName")}
            error={errors.driverName}
            parentClassName="col-span-1"
          />
          <Input
            label={t("label-driver-ktp")}
            required
            placeholder={t("placeholder-driver-ktp")}
            data-testid="input-driver-ktp"
            {...register("ktpNumber")}
            error={errors.ktpNumber}
            parentClassName="col-span-1"
          />
          <Input
            label={t("label-driver-code") || "Driver Code"}
            placeholder={t("placeholder-driver-code") || "Auto-generated on save"}
            {...register("driverId")}
            error={errors.driverId}
            parentClassName="col-span-1"
            disabled
          />
          <Controller
            name="joinDate"
            control={form.control}
            render={({ field, fieldState }) => (
              <DatePicker
                label={t("label-join-date")}
                placeholder="dd / mm / yyyy"
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error}
                parentClassName="col-span-1"
              />
            )}
          />

          <Controller
            name="phoneNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <PhoneInput
                id="input-driver-phone"
                label={t("label-driver-phone")}
                required
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                countryCode={form.watch("phoneCountryCode")}
                onCountryChange={(code) => form.setValue("phoneCountryCode", code)}
                placeholder={t("placeholder-driver-phone")}
                error={fieldState.error}
              />
            )}
          />

          <Controller
            name="gender"
            control={form.control}
            render={({ field, fieldState }) => (
              <Select
                label={t("label-gender")}
                options={[
                  { label: t("gender-men"), value: "Men" },
                  { label: t("gender-women"), value: "Women" },
                ]}
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                error={fieldState.error}
                placeholder={t("placeholder-select")}
              />
            )}
          />

          <Controller
            name="maritalStatus"
            control={form.control}
            render={({ field, fieldState }) => (
              <Select
                label={t("label-marital-status")}
                options={[
                  { label: t("marital-single"), value: "Single" },
                  { label: t("marital-married"), value: "Married" },
                  { label: t("marital-divorced"), value: "Divorced" },
                ]}
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                error={fieldState.error}
                placeholder={t("placeholder-select")}
              />
            )}
          />

          <Controller
            name="associatedVendor"
            control={form.control}
            render={({ field, fieldState }) => (
              <Select
                label={t("label-associated-vendor")}
                required
                data-testid="select-associated-vendor"
                options={vendorOptions}
                searchable
                loading={isLoadingVendorOptions}
                onSearch={onSearchVendors}
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                error={fieldState.error}
                placeholder={t("placeholder-select")}
              />
            )}
          />

          <div className="col-span-2">
            <Textarea
              label={t("label-domicile-address")}
              required
              placeholder={t("placeholder-address")}
              {...register("domicileAddress", {
                onChange: (event) => {
                  if (isSameAddress) {
                    form.setValue("citizenAddress", event.target.value, { shouldValidate: true });
                  }
                }
              })}
              error={errors.domicileAddress}
              rows={2}
            />
          </div>

          <div className="col-span-2 flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <Typography variant="bodyLarge">
                {t("label-citizen-address")} <span className="text-error-500">*</span>
              </Typography>
              <label className="inline-flex items-center gap-2 text-sm text-tertiary cursor-pointer">
                {t("label-same-address")}
                <input
                  type="checkbox"
                  checked={isSameAddress}
                  onChange={(event) => {
                    form.setValue("isSameAddress", event.target.checked);
                    if (event.target.checked) {
                      form.setValue("citizenAddress", form.getValues("domicileAddress"), { shouldValidate: true });
                    }
                  }}
                />
              </label>
            </div>
            <Textarea
              placeholder={t("placeholder-address")}
              {...register("citizenAddress")}
              error={errors.citizenAddress}
              rows={2}
              disabled={isSameAddress}
            />
          </div>

            <div className="col-span-4">
              <Controller
                name="status"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Select
                    label={t("label-status")}
                    required
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(value) => field.onChange(value)}
                    error={fieldState.error}
                    placeholder={t("placeholder-select")}
                  />
                )}
              />
            </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button type="button" variant="outline" color="tertiary" onClick={onNext}>
          {t("btn-next")}
        </Button>
      </div>
    </DriverAccordion>
  );
};

export default DriverProfileAccordion;
